import React, { useState, useEffect, useRef } from "react";
import { ReactComponent as CloseIcon } from "assets/images/icon-close.svg";
import { i18n } from "../../translate/i18n";
import { READING_ORDERS } from "utils/readingOrders";
import { usePlan } from "components/FormPlan/Context";

const Picker = ({ onChangePicker, readingValue }) => {
  const { readingOrder } = usePlan();

  const bibleBooks = READING_ORDERS[readingOrder];

  const [componentIsVisible, setComponentIsVisible] = useState(true);

  const myRef = useRef(null);
  const bookRefs = useRef([]);

  useEffect(() => {
    document.addEventListener("click", togglePicker);
    return () => {
      document.removeEventListener("click", togglePicker);
    };
  }, []);

  const togglePicker = (event) => {
    if (!myRef.current) return;

    let childrens = Array.from(myRef.current.querySelectorAll("*"));
    childrens.push(myRef.current);

    function matchClick() {
      for (let i = 0; i < childrens.length; i++) {
        if (event.target === childrens[i]) {
          return true;
        }
      }
      return false;
    }

    if (!matchClick()) {
      hidePicker();
    }

    if (myRef.current.previousElementSibling === event.target) {
      showPicker();
    }
  };

  // Get chapters array for the selected book
  const getChaptersArray = () => {
    const selectedBook = bibleBooks[readingValue.bookIndex];
    if (!selectedBook) {
      return [];
    }

    let array = [];
    for (let i = selectedBook.startChapter; i <= selectedBook.endChapter; i++) {
      array.push(i);
    }
    return array;
  };

  // Book and chapter selection handlers
  const giveBookActiveFeedback = (event) => {
    resetSelectedBookFeedBack();
    event.target.classList.add("active");
  };

  const giveChapterActiveFeedback = (event) => {
    resetSelectedChapterFeedBack();
    event.target.classList.add("active");
  };

  const resetSelectedChapterFeedBack = () => {
    let selectedChapter = document.querySelector(".chapter.active");
    !!selectedChapter && selectedChapter.classList.remove("active");
  };

  const resetSelectedBookFeedBack = () => {
    let selectedBook = document.querySelector(".book.active");
    !!selectedBook && selectedBook.classList.remove("active");
  };

  const handleBookListClick = (event) => {
    let bookIndex = Number(event.target.getAttribute("index"));
    let bookAbbreviation = event.target.getAttribute("abbreviation");
    let book = bibleBooks[bookIndex];
    let bookStartChapter = book.startChapter;
    let bibleReference = `${bookAbbreviation} ${bookStartChapter}`;

    resetSelectedChapterFeedBack();
    giveBookActiveFeedback(event);
    onChangePicker(bibleReference, bookIndex, bookStartChapter);
  };

  const handleChapterListClick = (event) => {
    let chapter = Number(event.target.getAttribute("chapter"));
    const selectedBook = bibleBooks[readingValue.bookIndex];
    let bibleReference = `${selectedBook.bookAbbreviation} ${chapter}`;

    giveChapterActiveFeedback(event);
    onChangePicker(bibleReference, readingValue.bookIndex, chapter);
    changeBiblePickerInput(
      selectedBook.bookAbbreviation,
      readingValue.bookIndex,
      chapter
    );
    hidePicker();
  };

  // Map books and chapters to JSX
  const mapBooks = (books) => {
    let listBooks = books.map((book, index) => {
      const bookRange =
        readingOrder === "chronological"
          ? book.startChapter !== book.endChapter
            ? `(${book.startChapter}-${book.endChapter})`
            : `(${book.startChapter})`
          : "";

      return (
        <span
          className={`book jsBookPick ${
            index === readingValue.bookIndex ? "active" : ""
          }`}
          onClick={handleBookListClick}
          abbreviation={book.bookAbbreviation}
          book-id={book.id}
          key={book.index + book.id}
          index={index}
          chapters={book.chapters}
          start-chapter={book.startChapter}
          end-chapter={book.endChapter}
          tabIndex={componentIsVisible ? 0 : -1}
          ref={(el) => (bookRefs.current[index] = el)}
        >
          {i18n.t(`messages.bibleBooks.${book.name}.name`)} {bookRange}
        </span>
      );
    });

    return listBooks;
  };

  const mapChapters = (chapters) => {
    let listChapters = chapters.map((chapter, index) => (
      <span
        className={`chapter jsChapterPick ${
          chapter === readingValue.chapter ? "active" : ""
        }`}
        chapter={chapter}
        onClick={handleChapterListClick}
        key={index + chapter}
        tabIndex={componentIsVisible ? 0 : -1}
      >
        {chapter}
      </span>
    ));

    return listChapters;
  };

  // Show/hide picker
  const showPicker = () => {
    setComponentIsVisible(true);
  };

  const hidePicker = () => {
    setComponentIsVisible(false);
  };

  // Update parent component
  const changeBiblePickerInput = (bookAbbreviation, bookIndex, chapter) => {
    let inputFormated = `${bookAbbreviation} ${chapter}`;
    onChangePicker(inputFormated, bookIndex, chapter);
  };

  // Render
  const amountChapters = getChaptersArray();
  const chapters = mapChapters(amountChapters);

  return (
    <div
      ref={myRef}
      className={`bible-picker jsBiblePicker ${
        componentIsVisible ? "" : "hidden"
      }`}
    >
      <div className="bible-picker__header">
        <span className="bible-picker__close" onClick={hidePicker}>
          <CloseIcon className="bible-picker__close-icon" />
        </span>
      </div>
      <div className="bible-picker__columns">
        <div className="bible-picker__column">
          <header className="column__header">
            <h4 className="header__title">
              {i18n.t("titles.biblePicker.book")}
            </h4>
          </header>
          <div className="books" tabIndex={-1}>
            {mapBooks(bibleBooks)}
          </div>
        </div>
        <div className="bible-picker__column" tabIndex={-1}>
          <header className="column__header">
            <h4 className="header__title">
              {i18n.t("titles.biblePicker.chapter")}
            </h4>
          </header>
          <div className="chapters">{chapters}</div>
        </div>
      </div>
    </div>
  );
};

export default Picker;
